const SCHEMA_VERSION = 2.0; // Match version in migrationPipeline.js
const VERSION_KEY = 'bagpipe_app_schema_version';
const STORAGE_INTEGRITY_CHECK_KEY = 'storage_integrity_last_check';
const STORAGE_RECOVERY_LOG_KEY = 'storage_recovery_log';

// Import migration pipeline systems
// Note: We're avoiding require() which can cause circular dependency issues
let migrationPipeline = null;
try {
  // We'll import it when needed instead of upfront
  console.log('Storage module initialized');
} catch (error) {
  console.warn('Error initializing storage module:', error);
}

/**
 * Check and safely access migration pipeline
 * @returns {Object|null} Migration pipeline or null if not available
 */
function getMigrationPipeline() {
  // Only try to load it if we haven't already
  if (!migrationPipeline) {
    try {
      // Dynamic import to avoid circular dependencies
      migrationPipeline = require('./migrationPipeline');
      console.log('Migration pipeline loaded successfully');
    } catch (error) {
      console.warn('Migration pipeline could not be loaded:', error);
      return null;
    }
  }
  return migrationPipeline;
}

/**
 * Safe method call with fallback
 * @param {string} methodName - Name of the method to call
 * @param {*} fallbackValue - Value to return if method doesn't exist
 * @param {Array} args - Arguments to pass to the method
 * @returns {*} Result of the method call or fallback value
 */
function safeMethodCall(methodName, fallbackValue, ...args) {
  const pipeline = getMigrationPipeline();
  if (pipeline && typeof pipeline[methodName] === 'function') {
    try {
      return pipeline[methodName](...args);
    } catch (error) {
      console.error(`Error calling ${methodName}:`, error);
      return fallbackValue;
    }
  }
  return fallbackValue;
}

/**
 * Initialize storage and run migrations if needed
 * @param {boolean} autoMigrate Whether to automatically run migrations
 * @returns {Object} Initialization result with migration info
 */
function initializeStorage(autoMigrate = true) {
  // Perform integrity check on startup
  const integrityResult = checkStorageIntegrity();
  if (!integrityResult.valid) {
    console.warn('Storage integrity check failed, attempting recovery:', integrityResult);
    recoverFromCorruption(integrityResult.corruptKeys);
  }
  
  // Get stored version or default to legacy version (1.0)
  const storedVersion = parseFloat(localStorage.getItem(VERSION_KEY));
  let migrationResult = null;
  let needsMigration = false;
  
  // Safely check if migration is needed
  needsMigration = safeMethodCall('needsMigration', false);
  
  if (needsMigration) {
    // Only try to initialize global settings if migration pipeline is available
    safeMethodCall('initializeGlobalSettings', null);
    
    if (autoMigrate) {
      console.log('Running automatic migrations during app initialization');
      try {
        // Perform automatic migrations
        migrationResult = safeMethodCall('runMigrations', null);
        if (migrationResult) {
          console.log('Automatic migrations completed:', migrationResult);
        }
      } catch (error) {
        console.error('Error during automatic migrations:', error);
      }
    }
    
    // Note: The new system updates VERSION_KEY automatically during migrations
    return {
      migrated: !!migrationResult,
      needsMigration: needsMigration && !migrationResult,
      storedVersion: safeMethodCall('getCurrentVersion', storedVersion),
      migrationResult,
      storageIntegrity: integrityResult
    };
  }
  
  return {
    migrated: false,
    needsMigration: false,
    storedVersion: storedVersion || 1.0,
    storageIntegrity: integrityResult
  };
}

/**
 * Get an item from localStorage with proper error handling
 * @param {string} key Key to get
 * @param {any} defaultValue Default value if key doesn't exist or is corrupted
 * @returns {any} The stored value or defaultValue if not found
 */
export function getItem(key, defaultValue = null) {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    
    // Basic validation for JSON structure before parsing
    if (typeof item === 'string') {
      // Simplistic check for malformed JSON
      if ((item.startsWith('{') && !item.endsWith('}')) || 
          (item.startsWith('[') && !item.endsWith(']')) ||
          (item.includes('undefined'))) {
        logRecoveryEvent(`Detected malformed JSON in key "${key}", using default value`);
        return defaultValue;
      }

      try {
        return JSON.parse(item);
      } catch (parseError) {
        logRecoveryEvent(`JSON parse error for key "${key}": ${parseError.message}`);
        return defaultValue;
      }
    }
    
    return defaultValue;
  } catch (error) {
    console.error(`Error getting "${key}" from localStorage:`, error);
    logRecoveryEvent(`Error retrieving key "${key}": ${error.message}`);
    return defaultValue;
  }
}

/**
 * Set an item in localStorage with proper error handling
 * @param {string} key Key to set
 * @param {any} value Value to store
 * @returns {boolean} true if successful, false otherwise
 */
export function setItem(key, value) {
  try {
    // Check if value is valid for serialization
    if (value === undefined) {
      console.warn(`Attempting to store undefined value for key "${key}"`);
      logRecoveryEvent(`Prevented storing undefined for key "${key}"`);
      return false;
    }

    // Test JSON serialization before storing
    const serialized = JSON.stringify(value);
    if (serialized === undefined) {
      console.warn(`Failed to serialize value for key "${key}"`);
      logRecoveryEvent(`Failed to serialize value for key "${key}"`);
      return false;
    }
    
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error(`Error setting "${key}" in localStorage:`, error);
    
    // Handle quota exceeded error specifically
    if (error.name === 'QuotaExceededError' || 
        error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
        error.message.includes('quota')) {
      logRecoveryEvent(`Storage quota exceeded for key "${key}"`);
      
      // Try to free up space by removing non-critical data
      try {
        removeNonCriticalData();
        
        // Try again after cleanup
        try {
          localStorage.setItem(key, JSON.stringify(value));
          return true;
        } catch (retryError) {
          // Still failed after cleanup
          logRecoveryEvent(`Failed to store key "${key}" even after cleanup`);
          return false;
        }
      } catch (cleanupError) {
        console.error('Error during storage cleanup:', cleanupError);
      }
    }
    
    logRecoveryEvent(`Failed to store key "${key}": ${error.message}`);
    return false;
  }
}

/**
 * Remove an item from localStorage with proper error handling
 * @param {string} key Key to remove
 * @returns {boolean} true if successful, false otherwise
 */
export function removeItem(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing "${key}" from localStorage:`, error);
    return false;
  }
}

/**
 * Remove non-critical data to free up storage space
 * Preserves essential app settings and user data
 */
function removeNonCriticalData() {
  const criticalKeys = [
    VERSION_KEY,
    'global_selected_microphone',
    'global_selected_chanter',
    'bagpipe_chanters',
    'bagpipe_global_gain',
    'bagpipe_user_uuid',
    'bagpipe_user_initials',
    'chantersync_preferences',
  ];
  
  // Log what we're about to do
  logRecoveryEvent('Removing non-critical data to free space');
  
  // Get all keys
  const allKeys = [];
  for (let i = 0; i < localStorage.length; i++) {
    allKeys.push(localStorage.key(i));
  }
  
  // Identify disposable keys (logs, caches, etc.)
  const disposableKeys = allKeys.filter(key => {
    return !criticalKeys.includes(key) && 
           (key.includes('log') || 
            key.includes('cache') || 
            key.includes('history') || 
            key.includes('recent'));
  });
  
  // Remove disposable keys first
  disposableKeys.forEach(key => {
    try {
      localStorage.removeItem(key);
      logRecoveryEvent(`Removed disposable key: ${key}`);
    } catch (error) {
      console.error(`Error removing disposable key ${key}:`, error);
    }
  });
  
  // If we still need more space, remove non-critical keys
  if (disposableKeys.length === 0) {
    const nonCriticalKeys = allKeys.filter(key => !criticalKeys.includes(key));
    
    // Remove oldest non-critical keys (we'll remove up to half)
    const keysToRemove = nonCriticalKeys.slice(0, Math.ceil(nonCriticalKeys.length / 2));
    
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
        logRecoveryEvent(`Removed non-critical key: ${key}`);
      } catch (error) {
        console.error(`Error removing non-critical key ${key}:`, error);
      }
    });
  }
}

/**
 * Check integrity of localStorage data
 * @returns {Object} Integrity check result with corrupt keys
 */
function checkStorageIntegrity() {
  const corruptKeys = [];
  const totalKeys = localStorage.length;
  let checkedKeys = 0;
  
  // Skip checking too frequently (once per day is enough)
  const lastCheck = parseInt(localStorage.getItem(STORAGE_INTEGRITY_CHECK_KEY) || '0');
  const now = Date.now();
  const oneDayMs = 24 * 60 * 60 * 1000;
  
  // Skip if checked within the last day, unless we're under 20 keys (small storage is quick to check)
  if (totalKeys > 20 && now - lastCheck < oneDayMs) {
    return { 
      valid: true, 
      skipped: true, 
      lastCheck: new Date(lastCheck).toISOString(),
      message: 'Integrity check skipped (checked within 24 hours)'
    };
  }
  
  // Check each key
  for (let i = 0; i < totalKeys; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    
    try {
      checkedKeys++;
      const item = localStorage.getItem(key);
      
      // Skip non-JSON items and internal keys
      if (key === VERSION_KEY || key === STORAGE_INTEGRITY_CHECK_KEY || key === STORAGE_RECOVERY_LOG_KEY) {
        continue;
      }
      
      // Basic validation for JSON structure
      if (typeof item === 'string') {
        // Skip empty items
        if (item === '') continue;
        
        // Try to parse - if it fails, mark as corrupt
        try {
          JSON.parse(item);
        } catch (error) {
          corruptKeys.push({ key, error: error.message });
        }
      }
    } catch (error) {
      console.error(`Error checking integrity of key "${key}":`, error);
      corruptKeys.push({ key, error: 'Access error' });
    }
  }
  
  // Update last check timestamp
  localStorage.setItem(STORAGE_INTEGRITY_CHECK_KEY, now.toString());
  
  return {
    valid: corruptKeys.length === 0,
    corruptKeys,
    totalKeys,
    checkedKeys,
    timestamp: new Date().toISOString()
  };
}

/**
 * Recover from storage corruption by removing or fixing corrupt keys
 * @param {Array} corruptKeys List of corrupt keys to fix
 */
function recoverFromCorruption(corruptKeys = []) {
  if (!corruptKeys || corruptKeys.length === 0) {
    const integrity = checkStorageIntegrity();
    if (integrity.valid) {
      logRecoveryEvent('No corruption detected during recovery check');
      return;
    }
    corruptKeys = integrity.corruptKeys;
  }
  
  logRecoveryEvent(`Attempting to recover ${corruptKeys.length} corrupt keys`);
  
  // Handle each corrupt key
  corruptKeys.forEach(entry => {
    const { key } = entry;
    
    // For known keys, attempt to restore defaults
    const knownDefaults = {
      'global_selected_microphone': null,
      'global_selected_chanter': 'Standard',
      'bagpipe_global_gain': '1.0',
      'bagpipe_app_schema_version': SCHEMA_VERSION.toString()
    };
    
    if (knownDefaults[key] !== undefined) {
      // This is a known key with a default value
      try {
        if (key === 'bagpipe_app_schema_version') {
          // This is a special case - not JSON
          localStorage.setItem(key, knownDefaults[key]);
        } else {
          localStorage.setItem(key, JSON.stringify(knownDefaults[key]));
        }
        logRecoveryEvent(`Restored default value for key "${key}"`);
      } catch (error) {
        console.error(`Failed to restore default for key "${key}":`, error);
        // Remove it if we can't restore it
        try {
          localStorage.removeItem(key);
          logRecoveryEvent(`Removed corrupt key "${key}" (could not restore default)`);
        } catch (removeError) {
          console.error(`Failed to remove corrupt key "${key}":`, removeError);
        }
      }
    } else {
      // For unknown keys, just remove them
      try {
        localStorage.removeItem(key);
        logRecoveryEvent(`Removed corrupt key "${key}"`);
      } catch (error) {
        console.error(`Failed to remove corrupt key "${key}":`, error);
      }
    }
  });
  
  // Verify recovery
  const postRecoveryCheck = checkStorageIntegrity();
  if (!postRecoveryCheck.valid) {
    logRecoveryEvent(`Recovery incomplete - ${postRecoveryCheck.corruptKeys.length} keys still corrupt`);
    
    // If critical keys are still corrupt, try clearing ALL non-essential data
    const criticalCorrupt = postRecoveryCheck.corruptKeys.some(entry => 
      ['global_selected_microphone', 'global_selected_chanter', 'bagpipe_app_schema_version'].includes(entry.key)
    );
    
    if (criticalCorrupt) {
      logRecoveryEvent('Critical keys still corrupt - performing emergency recovery');
      performEmergencyRecovery();
    }
  } else {
    logRecoveryEvent('Storage recovery completed successfully');
  }
}

/**
 * Emergency recovery - keep only the absolute minimum critical data
 * and reset to defaults
 */
function performEmergencyRecovery() {
  // Backup important user data if possible
  const backupData = {};
  try {
    // Try to save user identity
    const uuid = localStorage.getItem('bagpipe_user_uuid');
    if (uuid) backupData.uuid = uuid;
    
    const initials = getItem('bagpipe_user_initials', null);
    if (initials) backupData.initials = initials;
    
    // Try to save user chanters
    const chanters = getItem('bagpipe_chanters', []);
    if (chanters && Array.isArray(chanters) && chanters.length > 0) {
      backupData.chanters = chanters;
    }
    
    logRecoveryEvent('Backed up critical user data before emergency recovery');
  } catch (error) {
    console.error('Failed to backup user data:', error);
    logRecoveryEvent(`Backup failed: ${error.message}`);
  }
  
  // Clear everything
  try {
    localStorage.clear();
    logRecoveryEvent('Cleared all localStorage in emergency recovery');
    
    // Restore only the critical minimum
    localStorage.setItem(VERSION_KEY, SCHEMA_VERSION.toString());
    setItem('global_selected_microphone', null);
    setItem('global_selected_chanter', 'Standard');
    setItem('bagpipe_global_gain', 1.0);
    
    // Restore user data if available
    if (backupData.uuid) {
      localStorage.setItem('bagpipe_user_uuid', backupData.uuid);
    }
    
    if (backupData.initials) {
      setItem('bagpipe_user_initials', backupData.initials);
    }
    
    if (backupData.chanters) {
      setItem('bagpipe_chanters', backupData.chanters);
    }
    
    logRecoveryEvent('Emergency recovery completed with data restoration');
  } catch (error) {
    console.error('Failed emergency recovery:', error);
    logRecoveryEvent(`Emergency recovery failed: ${error.message}`);
    
    // Last resort - just set version
    try {
      localStorage.setItem(VERSION_KEY, SCHEMA_VERSION.toString());
    } catch (versionError) {
      console.error('Failed to set version after emergency recovery:', versionError);
    }
  }
}

/**
 * Log a recovery event with timestamp
 * @param {string} message Event message
 */
function logRecoveryEvent(message) {
  try {
    const logs = getItem(STORAGE_RECOVERY_LOG_KEY, []);
    
    // Keep only the most recent 50 logs
    if (logs.length > 50) {
      logs.splice(0, logs.length - 49);
    }
    
    logs.push({
      timestamp: new Date().toISOString(),
      message
    });
    
    setItem(STORAGE_RECOVERY_LOG_KEY, logs);
    
    // Also console log for debugging
    console.warn(`[Storage Recovery] ${message}`);
  } catch (error) {
    // If we can't even log properly, try a direct approach
    console.error('Failed to log recovery event:', error);
    try {
      // Simple string format to avoid parsing issues
      const timestamp = new Date().toISOString();
      const simpleLog = `${timestamp}: ${message}`;
      
      // Get existing log as string or create new
      const existingLog = localStorage.getItem(STORAGE_RECOVERY_LOG_KEY) || '[]';
      let logs = [];
      
      try {
        logs = JSON.parse(existingLog);
        // If it's not an array, reset
        if (!Array.isArray(logs)) logs = [];
      } catch (parseError) {
        logs = [];
      }
      
      logs.push({ timestamp, message });
      if (logs.length > 10) logs = logs.slice(-10); // Keep only most recent in emergency
      
      localStorage.setItem(STORAGE_RECOVERY_LOG_KEY, JSON.stringify(logs));
    } catch (finalError) {
      // Nothing more we can do
      console.error('Complete failure to log recovery events');
    }
  }
}

/**
 * Get the recovery log for diagnostic purposes
 * @returns {Array} Array of recovery log events
 */
export function getRecoveryLog() {
  return getItem(STORAGE_RECOVERY_LOG_KEY, []);
}

/**
 * Clear the recovery log
 * @returns {boolean} true if successful
 */
export function clearRecoveryLog() {
  return removeItem(STORAGE_RECOVERY_LOG_KEY);
}

/**
 * Run a manual storage integrity check
 * @returns {Object} Integrity check results
 */
export function runStorageHealthCheck() {
  const result = checkStorageIntegrity();
  
  // Force a check even if we normally would skip it
  if (result.skipped) {
    // Override the skip and do a real check
    localStorage.removeItem(STORAGE_INTEGRITY_CHECK_KEY);
    return checkStorageIntegrity();
  }
  
  return result;
}

/**
 * Get summary of localStorage usage
 * @returns {Object} Storage usage statistics
 */
export function getStorageStats() {
  try {
    const stats = {
      totalKeys: localStorage.length,
      totalSize: 0,
      largestKeys: [],
      criticalKeys: {},
      estimatedRemaining: 'Unknown'
    };
    
    // Calculate total size and identify largest items
    const keySizes = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      
      const value = localStorage.getItem(key);
      const size = (key.length + (value ? value.length : 0)) * 2; // UTF-16 uses 2 bytes per character
      
      stats.totalSize += size;
      keySizes.push({ key, size });
      
      // Track critical keys
      if (['global_selected_microphone', 'global_selected_chanter', 
           'bagpipe_app_schema_version', 'bagpipe_chanters'].includes(key)) {
        stats.criticalKeys[key] = {
          size,
          value: value ? (value.length > 100 ? value.substring(0, 97) + '...' : value) : null
        };
      }
    }
    
    // Sort by size and get top 5
    keySizes.sort((a, b) => b.size - a.size);
    stats.largestKeys = keySizes.slice(0, 5);
    
    // Estimate remaining space (5MB typical limit)
    const estimatedLimit = 5 * 1024 * 1024;
    stats.estimatedRemaining = Math.max(0, estimatedLimit - stats.totalSize);
    stats.percentUsed = Math.min(100, (stats.totalSize / estimatedLimit) * 100).toFixed(1) + '%';
    
    return stats;
  } catch (error) {
    console.error('Error getting storage stats:', error);
    return {
      error: error.message,
      totalKeys: localStorage.length
    };
  }
}

function clearStorage() {
  try {
    localStorage.clear();
    
    // Use migration pipeline's version if available, otherwise use legacy version
    const pipeline = getMigrationPipeline();
    const versionToSet = (pipeline && pipeline.SCHEMA_VERSION) || SCHEMA_VERSION;
    localStorage.setItem(VERSION_KEY, versionToSet.toString());
    
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
}

// Run initial check on module load
const initialCheckResult = checkStorageIntegrity();
if (!initialCheckResult.valid && !initialCheckResult.skipped) {
  console.warn('Initial storage integrity check failed:', initialCheckResult);
  // Don't auto-recover on module load to avoid potential issues
  // Actual recovery happens at app initialization
}

// Export the initialization function for use in app startup
export { initializeStorage, clearStorage };
